import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import { SURFACE } from '../../mesh/attributes';
import type { Frame, MeshBuilder } from '../../mesh/meshBuilder';
import {
  friaryEntryHeight,
  friaryGardenEntryHeight,
  friaryGardenRects,
} from '../../../site/cloisterFloor';
import type { LandmarkContext } from './landmarkFeature';
import { archesOf, buildArchBridge, type ArchBridgeSpec } from '../../terrain/archBridge';
import { gableRoof } from '../roofSolids';
import {
  boxSolid,
  horizontalCap,
  orientedFace,
  planPoint,
  prismSolid,
  pyramidOverRing,
  ringPoints,
  subFrame,
  triangulateRing,
} from '../solids';
import { buildVesselRoof, vesselOutline } from './churchVessel';
import { corners, type PlanRect } from './genericBlock';
import { centredWindow, gableWall, pointedWindows, rectWall, type MasonryStyles } from './masonry';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { meshFrameOf, toLayout, type PlanFrame } from './planFrame';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';
import readings from './minderbroedersPlan.json';
import { buildFriaryEnclosure, friaryEnclosureRings } from './minderbroedersEnclosure';
import {
  buildFriaryGroundRect,
  buildFriaryOrchard,
  minderbroedersTreePoints,
} from './minderbroedersGround';

export const MINDERBROEDERS = readings;
type Range = (typeof readings.ranges)[number];
type Arcade = { readonly front: readonly number[][]; readonly id?: string };
const WALK_TOP = 3.55;
// Keep closed storey shells separate by 3 mm instead of coincident internal faces.
const UPPER_FLOOR = WALK_TOP + 0.003;
const WALL_DEPTH = 0.35;

export function minderbroedersFrame(landmark: Landmark): PlanFrame {
  const x = landmark.params['anchorX'],
    y = landmark.params['anchorY'];
  if (typeof x !== 'number' || typeof y !== 'number')
    throw new Error('Minderbroeders lacks its component anchor');
  return { origin: [x, y], heading: landmark.heading, ring: [] };
}

function arcadeOf(r: Arcade): {
  frame: { u: number; v: number; yaw: number };
  spec: ArchBridgeSpec;
} {
  const a = r.front[0]!,
    b = r.front[1]!,
    length = Math.hypot(b[0]! - a[0]!, b[1]! - a[1]!);
  let count = Math.max(1, Math.floor(length / 3.6));
  if (r.id === 'west-range' && count % 2 === 0) count--;
  const spans = Array.from({ length: count }, () => 2.1);
  if (r.id === 'west-range') spans[Math.floor(count / 2)] = 2.8;
  const yaw = Math.atan2(b[1]! - a[1]!, b[0]! - a[0]!);
  return {
    frame: {
      u: a[0]! - (Math.sin(yaw) * WALL_DEPTH) / 2,
      v: a[1]! + (Math.cos(yaw) * WALL_DEPTH) / 2,
      yaw,
    },
    spec: {
      length,
      width: WALL_DEPTH,
      spans,
      pierWidth: 0.9,
      footY: 0,
      springY: 1.55,
      crownY: 2.95,
      deckY: () => WALK_TOP,
      parapet: { height: 0, thickness: 0 },
    },
  };
}

function arcadePiers(r: Arcade): Ring[] {
  const { frame: f, spec } = arcadeOf(r),
    cuts = [0, ...archesOf(spec).flatMap((a) => [a.left, a.right]), spec.length];
  const p = (x: number, depth: number): Vec2 => [
    f.u + Math.cos(f.yaw) * x - Math.sin(f.yaw) * depth,
    f.v + Math.sin(f.yaw) * x + Math.cos(f.yaw) * depth,
  ];
  return cuts
    .filter((_, i) => i % 2 === 0 && i + 1 < cuts.length)
    .map((lo, i) => {
      const hi = cuts[2 * i + 1]!;
      return [
        p(lo, -WALL_DEPTH / 2),
        p(hi, -WALL_DEPTH / 2),
        p(hi, WALL_DEPTH / 2),
        p(lo, WALL_DEPTH / 2),
      ];
    });
}

/** Actual buildings, arcade piers and trunks; the reservation itself is not a collision wall. */
export function minderbroedersObstacles(landmark: Landmark): Ring[] {
  const frame = minderbroedersFrame(landmark);
  return [
    corners(readings.church.nave),
    vesselOutline({ ...readings.church.choir, apse: 'polygonal' }),
    ...readings.rooms.map(corners),
    ...[...readings.ranges, readings.northWalk].flatMap(arcadePiers),
    ...minderbroedersTreePoints().map((p) => ringPoints(p, 0.15, 5, 0)),
    ...friaryEnclosureRings(),
  ].map((r) => r.map((p) => toLayout(frame, p)));
}

/** Foundations support built parts, never the full garden/cemetery reservation. */
export function minderbroedersFoundations(landmark: Landmark): Ring[] {
  const frame = minderbroedersFrame(landmark);
  return [
    corners(readings.church.nave),
    vesselOutline({ ...readings.church.choir, apse: 'polygonal' }),
    ...readings.ranges.map(corners),
    corners(readings.northWalk),
    corners(readings.court),
  ].map((r) => r.map((p) => toLayout(frame, p)));
}

function wallOpenings(length: number, sill: number, top: number): Ring[] {
  const bays = Math.max(1, Math.floor(length / 3.8));
  return Array.from({ length: bays }, (_, i) => {
    const x = ((i + 0.5) * length) / bays;
    return [
      [x - 0.42, sill],
      [x + 0.42, sill],
      [x + 0.42, top],
      [x - 0.42, top],
    ];
  });
}

function room(builder: MeshBuilder, frame: Frame, styles: MasonryStyles, r: PlanRect): void {
  const ring = corners(r);
  ring.forEach((from, i) => {
    const to = ring[(i + 1) % 4]!,
      length = Math.hypot(to[0] - from[0], to[1] - from[1]);
    // Adjacent rooms share an interior boundary, not two visible back-to-back walls.
    const shared = readings.rooms.some(
      (q) =>
        q !== r &&
        ((i === 0 && q.v1 === r.v0 && q.u0 <= r.u0 && q.u1 >= r.u1) ||
          (i === 1 && q.u0 === r.u1 && q.v0 <= r.v0 && q.v1 >= r.v1) ||
          (i === 2 && q.v0 === r.v1 && q.u0 <= r.u0 && q.u1 >= r.u1) ||
          (i === 3 && q.u1 === r.u0 && q.v0 <= r.v0 && q.v1 >= r.v1))
    );
    if (!shared)
      rectWall(
        builder,
        styles,
        frame,
        [from, to],
        [0, WALK_TOP],
        length < 2 ? [] : wallOpenings(length, 1.05, 2.45)
      );
  });
  horizontalCap(builder, styles.wall, frame, ring, 0.01, true);
  horizontalCap(builder, styles.wall, frame, ring, WALK_TOP, true);
}

function range(builder: MeshBuilder, frame: Frame, styles: MasonryStyles, r: Range): void {
  const ring = corners(r);
  ring.forEach((from, side) => {
    const to = ring[(side + 1) % 4]!,
      length = Math.hypot(to[0] - from[0], to[1] - from[1]);
    rectWall(
      builder,
      styles,
      frame,
      [from, to],
      [UPPER_FLOOR, r.eaves],
      wallOpenings(length, 4.3, Math.min(5.75, r.eaves - 0.2)),
      0
    );
  });
  const f = r.alongU ? frame : { ...frame, yaw: frame.yaw + Math.PI / 2 };
  const q = r.alongU ? r : { u0: r.v0, u1: r.v1, v0: -r.u1, v1: -r.u0 };
  gableRoof(builder, styles, f, {
    ...q,
    eaveY: r.eaves,
    ridgeY: r.ridge,
    ends: { start: true, end: true },
  });
  horizontalCap(
    builder,
    { ...styles.wall, surface: SURFACE.wood },
    frame,
    ring,
    UPPER_FLOOR,
    false
  );
  const { frame: a, spec } = arcadeOf(r);
  buildArchBridge(builder, styles.wall, subFrame(frame, a.u, a.v, a.yaw), spec);
}

function church(builder: MeshBuilder, frame: Frame, styles: MasonryStyles): void {
  const n = readings.church.nave,
    c = readings.church.choir,
    ring = corners(n);
  for (const side of [0, 2]) {
    const from = ring[side]!,
      to = ring[(side + 1) % 4]!;
    rectWall(
      builder,
      styles,
      frame,
      [from, to],
      [0, n.eaves],
      pointedWindows(n.u1 - n.u0, 4.8, side === 0 ? 5.8 : 2.7, 10.8)
    );
  }
  const west: readonly [Vec2, Vec2] = [ring[3], ring[0]],
    width = n.v1 - n.v0;
  gableWall(builder, styles, frame, west, { eaveY: n.eaves, apexY: n.ridge }, [
    ...centredWindow(width, 4.5, 13.3, 0.38),
    [
      [6.05, 0.1],
      [7.95, 0.1],
      [7.95, 3.2],
      [6.05, 3.2],
    ],
  ]);
  // Recessed timber entry; its position and joinery are estimates.
  const doorFrame = subFrame(frame, n.u0, n.v1, -Math.PI / 2);
  boxSolid(
    builder,
    { ...styles.opening, surface: SURFACE.wood },
    doorFrame,
    [6.09, 0.1, 0.22],
    [7.91, 3.19, 0.27],
    { top: true, bottom: true }
  );
  // The east end is open to the lower choir; keep only the visible shoulder and gable.
  const outline: Ring = [
    [0, 0],
    [width, 0],
    [width, n.eaves],
    [width / 2, n.ridge],
    [0, n.eaves],
  ];
  const hole: Ring = [
    [width / 2 - c.half, 0],
    [width / 2 + c.half, 0],
    [width / 2 + c.half, c.eaveY],
    [width / 2, c.ridgeY],
    [width / 2 - c.half, c.eaveY],
  ];
  for (const tri of triangulateRing(outline, [hole]))
    orientedFace(
      builder,
      styles.wall,
      frame,
      tri.map(([x, y]) => planPoint(n.u1, y, n.v0 + x)),
      [1, 0, 0]
    );
  gableRoof(builder, styles, frame, {
    ...n,
    eaveY: n.eaves,
    ridgeY: n.ridge,
    ends: { start: false, end: false },
  });
  const choir = { ...c, apse: 'polygonal' as const },
    q = vesselOutline(choir);
  q.slice(0, -1).forEach((from, i) => {
    const to = q[i + 1]!,
      length = Math.hypot(to[0] - from[0], to[1] - from[1]);
    rectWall(
      builder,
      styles,
      frame,
      [from, to],
      [0, c.eaveY],
      i === 0 || i === q.length - 2
        ? pointedWindows(length, 4.4, 2.5, 9.5)
        : centredWindow(length, 2.5, 9.5, 0.5)
    );
  });
  buildVesselRoof(builder, styles, frame, choir);
  const fleche = ringPoints([6, 45], 0.7, 8, Math.PI / 8);
  fleche.forEach((from, i) => {
    const to = fleche[(i + 1) % fleche.length]!,
      w = Math.hypot(to[0] - from[0], to[1] - from[1]);
    rectWall(
      builder,
      styles,
      frame,
      [from, to],
      [17.65, 20.6],
      centredWindow(w, 0.8, 2.5, 0.5),
      17.65
    );
  });
  pyramidOverRing(builder, styles, frame, fleche, 20.6, { plan: [6, 45], y: 24.8 });
}

/** Unequal U-shaped ranges, attached church walk and wider source-observed open grounds. */
export function buildMinderbroeders(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors & {
    ground: LinearRGB;
    garden: LinearRGB;
    timber: LinearRGB;
    foliage: LinearRGB;
  },
  ids: { edgeId: number; seed: number },
  tracery: TracerySink = NO_TRACERY,
  context: Pick<LandmarkContext, 'elevation' | 'elevationAt'> = {}
): void {
  const plan = minderbroedersFrame(landmark),
    frame = meshFrameOf(plan);
  const base = paintedChapelStyles(colors, ids, tracery),
    styles = { ...base, wall: { ...base.wall, surface: SURFACE.brick } };
  church(builder, frame, styles);
  readings.rooms.forEach((r) => {
    room(builder, frame, styles, r);
  });
  readings.ranges.forEach((r) => {
    range(builder, frame, styles, r);
  });
  const w = readings.northWalk,
    a = arcadeOf(w);
  buildArchBridge(builder, styles.wall, subFrame(frame, a.frame.u, a.frame.v, a.frame.yaw), a.spec);
  // A closed lean-to shell keeps its buried junctions behind the church and east range.
  orientedFace(
    builder,
    styles.roof,
    frame,
    [
      planPoint(w.u0, UPPER_FLOOR, w.v0),
      planPoint(w.u1, UPPER_FLOOR, w.v0),
      planPoint(w.u1, 5.1, w.v1),
      planPoint(w.u0, 5.1, w.v1),
    ],
    [0, 1, 1]
  );
  orientedFace(
    builder,
    styles.wall,
    frame,
    [
      planPoint(w.u0, UPPER_FLOOR, w.v0),
      planPoint(w.u0, UPPER_FLOOR, w.v1),
      planPoint(w.u0, 5.1, w.v1),
    ],
    [-1, 0, 0]
  );
  orientedFace(
    builder,
    styles.wall,
    frame,
    [
      planPoint(w.u1, UPPER_FLOOR, w.v0),
      planPoint(w.u1, UPPER_FLOOR, w.v1),
      planPoint(w.u1, 5.1, w.v1),
    ],
    [1, 0, 0]
  );
  orientedFace(
    builder,
    styles.wall,
    frame,
    [
      planPoint(w.u0, UPPER_FLOOR, w.v1),
      planPoint(w.u1, UPPER_FLOOR, w.v1),
      planPoint(w.u1, 5.1, w.v1),
      planPoint(w.u0, 5.1, w.v1),
    ],
    [0, 0, -1]
  );
  horizontalCap(
    builder,
    { ...styles.wall, surface: SURFACE.wood },
    frame,
    corners(w),
    UPPER_FLOOR,
    false
  );
  const ground = {
      ...ids,
      strokeAngle: 0,
      extent: [0, 0] as const,
      color: colors.ground,
      surface: SURFACE.street,
    },
    garden = { ...ground, color: colors.garden, surface: SURFACE.garden };
  for (const r of readings.walks) horizontalCap(builder, ground, frame, corners(r), 0.08, true);
  const terrain = (p: Vec2) =>
      (context.elevationAt?.(toLayout(plan, p)) ?? 0) - (context.elevation ?? 0),
    e = readings.entry,
    start = terrain([e.u0, (e.v0 + e.v1) / 2]) + 0.03;
  buildFriaryGroundRect(builder, ground, frame, e, ([u]) => friaryEntryHeight(u, start, 0.08));
  const g = readings.gardenEntry,
    gardenStart = terrain([(g.u0 + g.u1) / 2, g.v0]) + 0.03;
  buildFriaryGroundRect(builder, ground, frame, g, ([, v]) =>
    friaryGardenEntryHeight(v, gardenStart, 0.08)
  );
  prismSolid(builder, garden, frame, corners(readings.court), 0, 0.12, {
    top: true,
    bottom: false,
  });
  for (const r of friaryGardenRects())
    buildFriaryGroundRect(builder, garden, frame, r.rect, (p) => terrain(p) + r.height);
  buildFriaryEnclosure(builder, styles.wall, frame, terrain);
  buildFriaryOrchard(builder, ground, garden, frame, colors, terrain);
}
